import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Tab,
  Tabs,
  Button,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Code as CodeIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  BugReport as BugIcon,
  Settings as SettingsIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import CodeDevelopmentPage from '../../code/CodeDevelopmentPage';
import FileExplorer from '../../code/FileExplorer';
import { claudeCodeService } from '../../../services/claudeCodeService';
import { websocketService } from '../../../services/websocketService';

const DevGuidelines = ({ projectId, existingContent = '', onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [workspaceStatus, setWorkspaceStatus] = useState(null);
  const [devTasks, setDevTasks] = useState([]);
  const [isExecutingTask, setIsExecutingTask] = useState(false);
  const [executingTaskId, setExecutingTaskId] = useState(null);

  // Pre-configured development guideline tasks
  const developmentGuidelineTasks = [
    {
      id: 'setup-project-structure',
      title: 'Setup Project Structure',
      description: 'Create a standardized project structure with proper folder organization, configuration files, and development environment setup.',
      category: 'setup',
      priority: 'high',
      language: 'python',
      framework: 'fastapi',
      estimatedHours: 2
    },
    {
      id: 'implement-code-standards',
      title: 'Implement Code Standards',
      description: 'Create linting configuration, code formatting rules, pre-commit hooks, and coding style guidelines.',
      category: 'standards',
      priority: 'high',
      language: 'python',
      framework: 'fastapi',
      estimatedHours: 3
    },
    {
      id: 'setup-testing-framework',
      title: 'Setup Testing Framework',
      description: 'Configure unit testing, integration testing, and test coverage reporting for the project.',
      category: 'testing',
      priority: 'high',
      language: 'python',
      framework: 'pytest',
      estimatedHours: 4
    },
    {
      id: 'configure-cicd-pipeline',
      title: 'Configure CI/CD Pipeline',
      description: 'Setup automated testing, building, and deployment pipeline using GitHub Actions or similar.',
      category: 'devops',
      priority: 'medium',
      language: 'yaml',
      framework: 'github-actions',
      estimatedHours: 5
    },
    {
      id: 'implement-logging-monitoring',
      title: 'Implement Logging & Monitoring',
      description: 'Setup comprehensive logging, error tracking, and application monitoring systems.',
      category: 'monitoring',
      priority: 'medium',
      language: 'python',
      framework: 'fastapi',
      estimatedHours: 3
    },
    {
      id: 'create-documentation-standards',
      title: 'Create Documentation Standards',
      description: 'Establish API documentation, code comments standards, and developer onboarding guides.',
      category: 'documentation',
      priority: 'medium',
      language: 'markdown',
      framework: 'swagger',
      estimatedHours: 4
    }
  ];

  useEffect(() => {
    setDevTasks(developmentGuidelineTasks);
    loadWorkspaceStatus();
  }, [projectId]);

  const loadWorkspaceStatus = async () => {
    try {
      const status = await claudeCodeService.getWorkspaceStatus(projectId);
      setWorkspaceStatus(status);
    } catch (error) {
      console.error('Error loading workspace status:', error);
      setWorkspaceStatus({ workspace_exists: false, files: [], directory_tree: {} });
    }
  };

  const handleExecuteTask = async (task) => {
    setIsExecutingTask(true);
    setExecutingTaskId(task.id);
    
    try {
      // Execute the task using Claude Code service
      await claudeCodeService.generateCodeFromTask(
        projectId,
        task.description,
        task.category,
        task.language,
        task.framework,
        `Development Guidelines Task: ${task.title}. Focus on creating maintainable, scalable, and well-documented code following industry best practices.`
      );
      
      // Reload workspace status after task completion
      await loadWorkspaceStatus();
      
      if (onSave) {
        onSave(`Executed development guideline task: ${task.title}`);
      }
    } catch (error) {
      console.error('Error executing task:', error);
    } finally {
      setIsExecutingTask(false);
      setExecutingTaskId(null);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTasksView = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon color="primary" />
          Development Guidelines Tasks
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadWorkspaceStatus}
          size="small"
          variant="outlined"
        >
          Refresh Workspace
        </Button>
      </Box>

      <Grid container spacing={2}>
        {devTasks.map((task) => (
          <Grid item xs={12} sm={6} lg={4} key={task.id}>
            <Card 
              variant="outlined" 
              sx={{ 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    {task.title}
                  </Typography>
                  <Chip 
                    label={task.priority} 
                    size="small" 
                    color={task.priority === 'high' ? 'error' : 'default'} 
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                  {task.description}
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  <Chip label={task.category} size="small" variant="outlined" />
                  <Chip label={task.language} size="small" variant="outlined" />
                  {task.framework && <Chip label={task.framework} size="small" variant="outlined" />}
                  <Chip label={`${task.estimatedHours}h`} size="small" variant="outlined" />
                </Box>
                
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={isExecutingTask && executingTaskId === task.id ? <StopIcon /> : <PlayArrowIcon />}
                  onClick={() => handleExecuteTask(task)}
                  disabled={isExecutingTask}
                  sx={{ mt: 'auto' }}
                >
                  {isExecutingTask && executingTaskId === task.id ? 'Executing...' : 'Execute Task'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderWorkspaceView = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderIcon color="primary" />
          Project Workspace
        </Typography>
        <Box>
          <Chip 
            label={workspaceStatus?.workspace_exists ? 'Active' : 'Not Created'} 
            color={workspaceStatus?.workspace_exists ? 'success' : 'default'}
            sx={{ mr: 1 }}
          />
          <Chip 
            label={`${workspaceStatus?.file_count || 0} files`} 
            variant="outlined"
          />
        </Box>
      </Box>

      {workspaceStatus?.workspace_exists ? (
        <FileExplorer
          files={workspaceStatus.files || []}
          directoryTree={workspaceStatus.directory_tree || {}}
          onFileSelect={(filePath) => {
            console.log('Selected file:', filePath);
            // File viewing will be handled by the FileExplorer component
          }}
          projectId={projectId}
          height="500px"
        />
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <FolderIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Workspace Created
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Execute a development guideline task to create the project workspace and generate code files.
          </Typography>
          <Button
            variant="contained"
            startIcon={<BuildIcon />}
            onClick={() => setActiveTab(0)}
            sx={{ 
              textTransform: 'none !important'
             }}
          >
            Go to Tasks
          </Button>
        </Paper>
      )}
    </Box>
  );

  const renderCodeEditorView = () => (
    <Box sx={{ height: '600px' }}>
      <CodeDevelopmentPage
        projectId={projectId}
        initialTasks={devTasks}
        workspaceStatus={workspaceStatus}
        onTaskExecute={handleExecuteTask}
        onWorkspaceUpdate={loadWorkspaceStatus}
        showTaskPanel={false} // Hide task panel since we have our own
        compact={true} // Use compact mode for integration
      />
    </Box>
  );

  if (isMobile) {
    return (
      <Box sx={{ width: '100%' }}>
        <Paper elevation={0} sx={{ bgcolor: 'background.default' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Tasks" icon={<AssignmentIcon />} />
            <Tab label="Workspace" icon={<FolderIcon />} />
            <Tab label="Code Editor" icon={<CodeIcon />} />
          </Tabs>
        </Paper>

        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && renderTasksView()}
          {activeTab === 1 && renderWorkspaceView()}
          {activeTab === 2 && renderCodeEditorView()}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon color="primary" />
          Development Guidelines - Code Tasks
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Execute pre-configured development guideline tasks to set up project structure, coding standards, 
          testing frameworks, and CI/CD pipelines. View generated code and manage your development workspace.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ bgcolor: 'background.default' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Development Tasks" icon={<AssignmentIcon />} />
          <Tab label="Project Workspace" icon={<FolderIcon />} />
          <Tab label="Code Editor" icon={<CodeIcon />} />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {activeTab === 0 && renderTasksView()}
        {activeTab === 1 && renderWorkspaceView()}
        {activeTab === 2 && renderCodeEditorView()}
      </Box>
    </Box>
  );
};

export default DevGuidelines;